import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { defaultOptions } from './constants/constants';
import Header from '../common/Header.jsx';
import Footer from '../common/Footer.jsx';
import { defaultDescription, defaultTitle } from '../constants/constants';
import { setTabIndex } from '../../helpers/accessibility_helper';
import { useNumberKeySelectorCallback } from '../../helpers/number_key_selector_helper';
import { useKeySelect } from '../../helpers/key_select_helper';
import { DEFAULT_TRANSLATION } from '../../helpers/constants';

const useStyles = makeStyles((theme) => ({
  '@keyframes blinker': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  blink: {
    animation: '$blinker 200ms linear 0s 2',
  },
  textContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: 'fit-content',
    minWidth: 271,
    maxWidth: '35rem',
  },
  optionContainer: {
    width: 'auto',
    minHeight: 48,
    border: `1px solid ${theme.palette.mainaccent || theme.legacyPalette.mainaccent}`,
    borderBottom: 'none',
    borderRadius: '0',
    boxSizing: 'border-box',
    margin: '0',
    textTransform: 'none',
    justifyContent: 'left',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    '&:first-child': {
      borderRadius: '4px 4px 0 0',
    },
    '&:last-child': {
      borderRadius: '0 0 4px 4px',
      borderBottom: `1px solid ${theme.palette.mainaccent || theme.legacyPalette.mainaccent}`,
    },
  },
  optionText: {
    display: 'flex',
    alignContent: 'center',
    alignItems: 'center',
    paddingLeft: 14,
    height: '100%',
    color: 'inherit',
    fontSize: 15,
    textAlign: 'left',
    whiteSpace: 'normal',
    textTransform: 'none',
  },
  selectedOptionText: {
    color: theme.palette.common.white,
    backgroundColor: '#0F7B85',
    '&:hover': {
      color: theme.palette.common.white,
      backgroundColor: '#0F7B85',
    },
  },
  unselectedOptionText: {
    color: '#000',
    backgroundColor: '#ECF6F7',
    '&:hover': {
      color: theme.palette.common.white,
      backgroundColor: '#0F7B85',
    },
  },
}));

const MultipleChoicePreview = ({
  questionId = -1,
  showSkipButton = false,
  options = defaultOptions,
  showTextComment = false,
  handleAnswer = () => {},
  onCommentChange = () => {},
  savedAnswer,
  isRequired = false,
  showDescription = false,
  title = defaultTitle,
  titleRef,
  titleAriaLabel,
  commentAriaLabel,
  description = defaultDescription,
  minSelectedOption = 1,
  maxSelectedOption = 1,
  allowKeyboardSelection = true,
  isActive = true,
  commentsLabel,
  commentsPlaceholder,
  translation = DEFAULT_TRANSLATION,
}) => {
  const styles = useStyles();

  const emptyAnswer = { value: [], questionId, comment: '' };
  const [answer, setAnswer] = useState(savedAnswer || emptyAnswer);
  const [, selectAnswer] = useState(false);

  const numberKeySelector = useNumberKeySelectorCallback(1, options.length, (key) => {
    handleAnswerSelection(key - 1);
  });

  useKeySelect(allowKeyboardSelection, numberKeySelector);

  const handleAnswerSelection = (index) => {
    let newValue;
    const selectedId = options[index].id;
    if (answer.value.includes(selectedId)) {
      newValue = answer.value.filter((ans) => ans !== selectedId);
    } else if (answer.value.length === maxSelectedOption) {
      if (maxSelectedOption === 1) {
        newValue = [selectedId];
      } else {
        newValue = answer.value;
      }
    } else {
      newValue = [...answer.value, selectedId];
    }
    const newAnswer = { ...answer, value: newValue };
    setAnswer(newAnswer);
    selectAnswer(true);
    handleAnswer(newAnswer, !showTextComment && newAnswer.value.length === maxSelectedOption);
  };

  const handleOptionalComment = (event) => {
    const newAnswer = { ...answer, comment: event.target.value };
    setAnswer(newAnswer);
    onCommentChange(event.target.value);
  };

  const onConfirm = (hasAnswer = false) => {
    if (hasAnswer) {
      handleAnswer(answer);
    } else {
      setAnswer(emptyAnswer);
      selectAnswer(true);
      handleAnswer(emptyAnswer);
    }
  };

  const shouldRenderOkButton =
    answer.value.length !== 0 &&
    answer.value.length >= minSelectedOption &&
    answer.value.length <= maxSelectedOption &&
    (showTextComment || maxSelectedOption > 1);

  return (
    <div>
      <Header
        isRequired={isRequired}
        title={title}
        titleRef={titleRef}
        titleAriaLabel={titleAriaLabel}
        questionId={questionId}
        description={description}
        showDescription={showDescription}
        isActive={isActive}
        translation={translation}
      />

      <div className={styles.inputContainer}>
        {options.map((option, index) => {
          return (
            <Button
              onClick={() => handleAnswerSelection(index)}
              className={classNames(
                styles.textContainer,
                styles.optionContainer,
                answer && answer.value.includes(option.id) && styles.blink,
                answer && answer.value.includes(option.id) ? styles.selectedOptionText : styles.unselectedOptionText,
              )}
              key={option.id}
              tabIndex={setTabIndex(isActive)}
              aria-hidden={!isActive}
              aria-label={`Click to select ${option.text}`}
              aria-pressed={answer?.value.includes(option.id)}
            >
              <Typography className={styles.optionText}>{option.text}</Typography>
            </Button>
          );
        })}
      </div>

      <Footer
        value={answer.value}
        showSkipButton={showSkipButton && answer.value.length === 0}
        showOkButton={shouldRenderOkButton}
        onConfirm={onConfirm}
        showTextComment={showTextComment}
        commentAriaLabel={commentAriaLabel}
        optionalComment={answer.comment}
        onChangeComment={handleOptionalComment}
        isActive={isActive}
        label={commentsLabel}
        placeholder={commentsPlaceholder}
        translation={translation}
      />
    </div>
  );
};

MultipleChoicePreview.propTypes = {
  questionId: PropTypes.number,
  title: PropTypes.string,
  description: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      position: PropTypes.number,
      text: PropTypes.string,
    }),
  ),
  minSelectedOption: PropTypes.number,
  maxSelectedOption: PropTypes.number,
  savedAnswer: PropTypes.object,
  handleAnswer: PropTypes.func,
  onCommentChange: PropTypes.func,
  showTextComment: PropTypes.bool,
  showSkipButton: PropTypes.bool,
  allowKeyboardSelection: PropTypes.bool,
  isRequired: PropTypes.bool,
  isActive: PropTypes.bool,
};

export default MultipleChoicePreview;
